import type { TMediaImage, TMediaVideo } from '~constants/media';
import type {
	GeneratedMedia,
	MediaPageData,
	MediaVisibility,
} from '~entities/media';

const apiBase = process.env.PUBLIC_API ?? 'https://api.ifriend.ai';

export type GetGeneratedMediaByIdApiResponse = (
	| { type: 'image'; imageLinks: TMediaImage[] }
	| { type: 'video'; mediaLinks: TMediaVideo[] }
) &
	Omit<GeneratedMedia, 'type' | 'isPublic' | 'mediaLinks'> & {
		visibility: MediaVisibility | null;
	};

export const getSingleMediaPageData = async (
	generationId: string,
	params?: RequestInit
) => {
	const response = await fetch(
		`${apiBase}/ai/generation/media/public/${generationId}`,
		{
			...params,
			cache: 'no-store',
		}
	);

	// 400 - user entered invalid slug
	// 404 - page doesn't exist OR rated as "xx" or "xxx"
	// 409 - private media
	if (![200, 400, 404, 409].includes(response.status)) {
		throw new Error(`Error fetching media: ${response.status}`);
	}

	const mediaPageData =
		response.status === 200
			? prepareMediaPageData(
					(await response.json()) as GetGeneratedMediaByIdApiResponse
			  )
			: undefined;

	return {
		status: response.status,
		mediaPageData,
	};
};

export const prepareMediaPageData = ({
	visibility,
	...mediaData
}: GetGeneratedMediaByIdApiResponse): MediaPageData =>
	({
		media: {
			...mediaData,
			mediaLinks:
				'imageLinks' in mediaData ? mediaData.imageLinks : mediaData.mediaLinks,
			isPublic: !visibility || visibility.status === 'public',
		},
		visibility,
	} satisfies MediaPageData);
